/**
 * Eye-shape classifier driven by MediaPipe Face Landmarker.
 *
 * Outputs one of six categories that match
 * `/app/backend/eye_shape_profile.py::VALID_SHAPES`:
 *   • hooded     — visible lid / brow-bone ratio is low (hood covers
 *                  the moveable lid when the eye is open)
 *   • monolid    — minimal crease deflection: the upper-lid contour
 *                  from inner-corner → midpoint → outer-corner is
 *                  near-flat (no perceptible vertical "lift" where
 *                  a crease would sit)
 *   • round      — eye_height / eye_width close to 1
 *   • almond     — standard eye_height / eye_width ratio, inner ≈
 *                  outer corner Y, mild lid visibility
 *   • downturned — outer corner sits significantly BELOW inner corner
 *   • upturned   — outer corner sits significantly ABOVE inner corner
 *
 * Privacy: landmarks never leave the device. The returned shape +
 * confidence are passed up to `/api/profile/eye-shape` as
 * `source="landmark"`, which is treated as advisory only — never
 * overwrites a "user_set" pin.
 *
 * Same returning-band conventions as faceShape.js: `confidence` is
 * one of "high" | "medium" | "low" | "unknown" so the caller can
 * decide whether to surface the suggestion.
 */

// MediaPipe Face Landmarker 478-point mesh indices for the LEFT eye
// (user-perspective right eye on the rendered canvas — the model
// labels them as the eyes in the FACE the camera sees, so what we
// call "left" here is the *subject's* left = our right when mirrored).
const L = {
  innerCorner: 133,   // left eye inner canthus
  outerCorner: 33,    // left eye outer canthus
  upperLidMid: 159,   // top of the visible eye opening
  lowerLidMid: 145,   // bottom of the visible eye opening
  brow: 105,          // mid-brow point ABOVE this eye
};

const R = {
  innerCorner: 362,
  outerCorner: 263,
  upperLidMid: 386,
  lowerLidMid: 374,
  brow: 334,
};

const NOSE_TIP = 1;

const dist = (a, b) => {
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
};

const computeOneEyeMetrics = (landmarks, idx, faceWidth) => {
  const inner = landmarks[idx.innerCorner];
  const outer = landmarks[idx.outerCorner];
  const upper = landmarks[idx.upperLidMid];
  const lower = landmarks[idx.lowerLidMid];
  const brow = landmarks[idx.brow];
  if (!inner || !outer || !upper || !lower || !brow) return null;

  const eyeWidth = dist(inner, outer);
  const eyeHeight = dist(upper, lower);
  if (eyeWidth === 0 || faceWidth === 0) return null;

  // Eye corner tilt: positive => outer corner sits ABOVE inner corner
  // (in image coordinates Y grows DOWN, so outer.y < inner.y means lifted).
  // Normalise by eye width so head tilt doesn't dominate the signal.
  const cornerTilt = (inner.y - outer.y) / eyeWidth;

  // Eyelid visibility ratio: distance from upper lid to brow,
  // normalised by face width. Hooded eyes have low brow-to-lid
  // distance relative to face size; monolid eyes have a smooth
  // unbroken plane (also low) but no crease deflection.
  const browToLid = (upper.y - brow.y) / faceWidth;

  // Crease deflection — how much the upper lid line "bows" between
  // inner and outer corners. We measure the perpendicular distance
  // from `upper` to the chord between inner and outer corners. A
  // monolid is near-zero; an almond/hooded has a clear deflection.
  // (Computed in image-Y units, normalised by eye width.)
  const chordSlope = (outer.y - inner.y) / (outer.x - inner.x || 1e-6);
  const chordIntercept = inner.y - chordSlope * inner.x;
  const lineY = chordSlope * upper.x + chordIntercept;
  const creaseDeflection = (lineY - upper.y) / eyeWidth;

  return {
    eyeWidth,
    eyeHeight,
    aspect: eyeHeight / eyeWidth,
    cornerTilt,
    browToLid,
    creaseDeflection,
  };
};

/**
 * Average metrics across both eyes — gives a stable signal even on
 * slightly off-frontal faces.
 */
export const computeEyeShapeMetrics = (landmarks) => {
  if (!landmarks || landmarks.length < 478) return null;

  // Use face width (cheekbone-to-cheekbone-ish) for normalisation —
  // matches faceShape.js conventions.
  const faceWidth = dist(landmarks[33], landmarks[263]) || 1;

  // Yaw gate: if the face is strongly turned, the eye-corner Y
  // comparison becomes meaningless (perspective foreshortens one eye).
  // We use the nose-tip vs midpoint-of-eye-corners check.
  const midX = (landmarks[33].x + landmarks[263].x) / 2;
  const yaw = Math.abs(landmarks[NOSE_TIP].x - midX) / faceWidth;

  const ml = computeOneEyeMetrics(landmarks, L, faceWidth);
  const mr = computeOneEyeMetrics(landmarks, R, faceWidth);
  if (!ml && !mr) return null;
  const ok = [ml, mr].filter(Boolean);

  const avg = (k) => ok.reduce((acc, m) => acc + m[k], 0) / ok.length;

  return {
    aspect: avg("aspect"),
    cornerTilt: avg("cornerTilt"),
    browToLid: avg("browToLid"),
    creaseDeflection: avg("creaseDeflection"),
    yaw,
    eyesAveraged: ok.length,
  };
};

/**
 * Classify the eye shape from the metrics. Returns
 *   { shape: string, confidence: "high" | "medium" | "low" | "unknown" }
 *
 * The thresholds below were chosen by hand from a set of reference
 * landmarks across all six categories — they're intentionally
 * conservative: any borderline case downgrades to "low" so we never
 * surface a false confident suggestion. The user's `user_set` value
 * always wins anyway.
 */
export const classifyEyeShape = (landmarks) => {
  const m = computeEyeShapeMetrics(landmarks);
  if (!m) return { shape: null, confidence: "unknown", metrics: null };

  // Non-frontal face → metrics are unreliable.
  if (m.yaw > 0.18) return { shape: null, confidence: "unknown", metrics: m };

  // 1. Corner tilt dominates: downturned / upturned override aspect.
  //    Threshold ~0.06 of eye width = ~3-4° tilt — well above noise.
  if (m.cornerTilt < -0.06) {
    return { shape: "downturned", confidence: m.cornerTilt < -0.10 ? "high" : "medium", metrics: m };
  }
  if (m.cornerTilt > 0.06) {
    return { shape: "upturned", confidence: m.cornerTilt > 0.10 ? "high" : "medium", metrics: m };
  }

  // 2. Round vs not — aspect ratio > 0.45 is unusually tall for an eye.
  if (m.aspect > 0.45) {
    return { shape: "round", confidence: m.aspect > 0.50 ? "high" : "medium", metrics: m };
  }

  // 3. Monolid vs hooded vs almond: use crease deflection + brow-to-lid.
  //    creaseDeflection ≈ 0  => no visible crease (monolid)
  //    creaseDeflection > 0  => upper lid arcs ABOVE the chord
  //                              (normal crease)
  //    browToLid is small for both hooded AND monolid; deflection
  //    distinguishes them.
  if (Math.abs(m.creaseDeflection) < 0.04) {
    return { shape: "monolid", confidence: Math.abs(m.creaseDeflection) < 0.02 ? "high" : "medium", metrics: m };
  }

  if (m.browToLid < 0.07) {
    // Low brow-to-lid distance + visible crease deflection → hooded
    return { shape: "hooded", confidence: m.browToLid < 0.055 ? "high" : "medium", metrics: m };
  }

  // 4. Default = almond
  return { shape: "almond", confidence: "medium", metrics: m };
};

export default classifyEyeShape;
